import { Alert, AlertTitle, Box, Button, Divider, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { GridCellParams, GridColDef, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { getFamily } from 'src/shared/services/familyService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { useGlobals } from 'src/shared/contexts/appContext';
import { MemberStatusEnum } from 'src/shared/models/enums/memberStatusEnum';
import { ManageAccounts, Person } from '@mui/icons-material';
import { RootState } from 'src/shared/services/store';
import { useSelector } from 'react-redux';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { FamilyMemberNew } from '../models/familyMember';
import { MemberFamilyDeleteFamilyMemberModal } from './memberFamilyDeleteFamilyMemberModal';
import { MemberFamilyChangeMainMemberModal } from './memberFamilyChangeMainMemberModal';
// import { MemberStatuses } from './memberStatuses';

type MemberMeFamilyDataGridProps = {
  mainMemberId: number | undefined;
  callbackNewMemberCount?: (count: number) => void;
  callbackUnpaidNewMembers?: (count: number) => void;
};

export const MemberMeFamilyDataGrid = (props: MemberMeFamilyDataGridProps) => {
  const { mainMemberId, callbackNewMemberCount = undefined, callbackUnpaidNewMembers = undefined } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const user = useSelector((state: RootState) => state.oidc.user);
  const { dateStamp } = useGlobals();

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'member_id', sort: 'asc' }]);
  const [selectedMember] = useState<FamilyMemberNew>();
  const [openDeleteFamilyMemberModal, setOpenDeleteFamilyMemberModal] = useState(false);
  const [openChangeMemberTypeModal, setOpenChangeMemberTypeModal] = useState<boolean>(false);
  const [newMemberCount, setNewMemberCount] = useState<number>(0);

  const [rows, setRows] = useState<FamilyMemberNew[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: false,
    totalRows: 0,
    rowsPerPageOptions: [10, 25, 50, 100],
    pageSize: 10,
    page: 0,
  });

  const columns: GridColDef[] = [
    { field: 'memberNr', flex: 1, sortable: false, headerName: t('member:tableHeaders.member_id') },
    {
      field: 'firstName',
      flex: 1,
      sortable: false,
      headerName: t('member:tableHeaders.name'),
      renderCell: (params: GridCellParams) => {
        const { firstName, lastName, mainMember } = params.row;
        return (
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
            <span>
              {firstName || ''} {lastName || ''}
            </span>
            {mainMember ? (
              <Tooltip title={t('member:memberDetails.headMember')} arrow>
                <ManageAccounts fontSize='small' />
              </Tooltip>
            ) : (
              <Tooltip title={t('member:memberDetails.familyMember')} arrow>
                <Person fontSize='small' />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'fee',
      flex: 0.75,
      sortable: false,
      type: 'number',
      headerName: t('common:organization.pageTabs.fee'),
      renderCell: (params: GridCellParams) => {
        const { fee } = params.row;
        return (
          <Typography>
            <AmountWithCurrency amount={fee ?? 0} />
          </Typography>
        );
      },
    },
    {
      field: 'unpaidInvoiceOcr',
      flex: 0.75,
      sortable: false,
      type: 'number',
      headerName: t('invoice:unpaidInvoice'),
      renderCell: (params: GridCellParams) => {
        const { unpaidInvoiceOcr } = params.row;
        return (
          <Tooltip title={unpaidInvoiceOcr ? t('member:family.findInvoiceTooltip') : ''} arrow>
            <span>{unpaidInvoiceOcr ?? '-'}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'unpaidInvoiceGuid',
      headerName: t('invoice:payOnline'),
      sortable: false,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        const { unpaidInvoiceId, unpaidInvoiceGuid } = params.row;

        const FSPayLink = `${process.env.REACT_APP_FS_PAY_URL}/${unpaidInvoiceId}/${unpaidInvoiceGuid}/${user?.profile.custname}`;
        return (
          <Box py={1}>
            {unpaidInvoiceGuid ? (
              <Button type='button' variant='outlined' size='small' href={FSPayLink} target='_blank'>
                {t('Betala')}
              </Button>
            ) : null}
          </Box>
        );
      },
    },
    // {
    //   field: 'status',
    //   headerName: t('member:tableHeaders.memberStatus'),
    //   sortable: false,
    //   flex: 0.75,
    //   renderCell: (params: GridCellParams) => {
    //     const { status: stat } = params.row;
    //     return <MemberStatuses status={stat} resDate='' chipSize='small' />;
    //   },
    // },
    {
      field: 'member_id',
      flex: 2,
      headerName: '',
      align: 'right',
      sortable: false,
      renderCell: () => {
        return <></>;
      },
    },
  ];

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  // const onPageChange = (newPageIndex: number) => {
  //   updateData('page', newPageIndex);
  // };

  // const onPageSizeChange = (newPageSize: number) => {
  //   updateData('page', 0);
  //   updateData('pageSize', newPageSize);
  // };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    ConsoleHelper.log('mainMemberId in datagrid fetchData', mainMemberId);
    try {
      if (mainMemberId) {
        const res = await getFamily(data.page, data.pageSize, sortModel[0].field, sortModel[0].sort?.toString(), mainMemberId);
        if (isMounted()) {
          updateData('totalRows', res.data.totalItems);
          const memberResult = extendObjectWithId(res.data.results, 'memberNr');
          ConsoleHelper.log('Fetch members for mainmember id', mainMemberId, memberResult);
          setRows(memberResult);
          const countNewMembers = memberResult.filter((obj) => obj.status.id === MemberStatusEnum.NewMember).length;
          const countUnpaidNewMembers = memberResult.filter(
            (obj) => obj.status.id === MemberStatusEnum.NewMember && obj.fee > 0 && !obj.isIncludedInUnpaidInvoice,
          ).length;
          // Om (status === intresseamnäld && fee > 0 && !hasUnpaidInvoice) -> visa "Gå vidare till betalning" annars "Slutför"
          ConsoleHelper.log('countUnpaidNewMembers', countUnpaidNewMembers);
          ConsoleHelper.log('countNewMembers', countNewMembers);
          setNewMemberCount(countNewMembers);
          if (callbackNewMemberCount) callbackNewMemberCount(countNewMembers);
          if (callbackUnpaidNewMembers) callbackUnpaidNewMembers(countUnpaidNewMembers);
        }
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error, dateStamp);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [data.page, data.pageSize, sortModel, mainMemberId, isMounted, callbackNewMemberCount, callbackUnpaidNewMembers, dateStamp]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box>
      {newMemberCount > 0 && (
        <>
          <Alert severity='info'>
            <AlertTitle>{t('member:newMemberPaymentAlertHeader')}</AlertTitle>
            {t('member:newMemberPaymentAlertText')}
          </Alert>
          <Divider />
        </>
      )}
      <DataGrid<FamilyMemberNew>
        autoHeight
        columnVisibilityModel={{
          member_id: false,
        }}
        columns={columns}
        rows={rows}
        dataModel={data}
        disableSelectionOnClick
        checkboxSelection={false}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
        selectionModel={selectionModel}
        onPageChange={() => ConsoleHelper.log('herj')}
        onPageSizeChange={() => ConsoleHelper.log('herj')}
      />
      {selectedMember && openDeleteFamilyMemberModal && (
        <MemberFamilyDeleteFamilyMemberModal
          member={selectedMember}
          modalOpen={openDeleteFamilyMemberModal}
          onClose={() => setOpenDeleteFamilyMemberModal(false)}
          refetchOnDelete={() => {
            fetchData();
            setOpenDeleteFamilyMemberModal(false);
          }}
        />
      )}
      {selectedMember && openChangeMemberTypeModal && (
        <MemberFamilyChangeMainMemberModal
          onClose={() => setOpenChangeMemberTypeModal(false)}
          member={selectedMember}
          modalOpen={openChangeMemberTypeModal}
          successCallback={() => {
            fetchData();
            setOpenChangeMemberTypeModal(false);
          }}
        />
      )}
    </Box>
  );
};
